import type { Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

/**
 * The parts of the page the browser draws for us, brought into the same design.
 *
 * Scrollbars, number spinners, text selection, focus rings and the autofill wash are all
 * chrome the browser picks colours for on its own — usually from the operating system, which
 * knows nothing about this palette. On a dark theme they are the pieces that give away that
 * the theme is only skin deep.
 *
 * Everything here is theme-driven rather than hard-coded: the same rules have to look right
 * in both palettes, and a literal colour would be right in one of them at most.
 */
export function baseline(theme: Theme) {
  const track = theme.palette.mode === 'dark' ? theme.palette.background.default : '#e9ecf5';

  const thumb = alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.22 : 0.26);

  const thumbHover = alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.34 : 0.4);

  return {
    // Firefox has no pseudo-elements for scrollbars, only these two properties. They are
    // ignored elsewhere, so both mechanisms live side by side.
    '*': {
      scrollbarWidth: 'thin' as const,
      scrollbarColor: `${thumb} ${track}`,
    },

    '*::-webkit-scrollbar': {
      width: 10,
      height: 10,
    },

    '*::-webkit-scrollbar-track': {
      backgroundColor: track,
    },

    '*::-webkit-scrollbar-thumb': {
      backgroundColor: thumb,
      borderRadius: 8,
      // The border is the track showing through, which keeps the thumb from touching the
      // edges and makes a 10px bar read as a 6px one.
      border: `2px solid ${track}`,
      backgroundClip: 'padding-box' as const,
      '&:hover': { backgroundColor: thumbHover },
    },

    '*::-webkit-scrollbar-corner': {
      backgroundColor: track,
    },

    // Number fields: the native spinners are tiny, differently shaped in every browser, and
    // sit on top of the field's own border. Counters here are set by the counter widget or
    // by typing, so nothing is lost by removing them.
    'input[type=number]': {
      MozAppearance: 'textfield' as const,
    },

    'input[type=number]::-webkit-outer-spin-button, input[type=number]::-webkit-inner-spin-button':
      {
        WebkitAppearance: 'none' as const,
        margin: 0,
      },

    // The date and search widgets ship their own icons in the browser's colours; invert them
    // on a dark background so they stop being black squares.
    ...(theme.palette.mode === 'dark'
      ? {
          'input[type=date]::-webkit-calendar-picker-indicator, input[type=search]::-webkit-search-cancel-button':
            { filter: 'invert(1) opacity(0.6)' },
        }
      : {}),

    '::selection': {
      backgroundColor: alpha(theme.palette.primary.main, 0.32),
      color: theme.palette.text.primary,
    },

    // Chrome paints autofilled fields a fixed pale yellow. There is no property to change
    // it — the accepted trick is an inset shadow large enough to cover the field, plus a
    // very long transition so the browser's own repaint never wins.
    'input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus': {
      WebkitBoxShadow: `0 0 0 100px ${theme.palette.background.paper} inset`,
      WebkitTextFillColor: theme.palette.text.primary,
      caretColor: theme.palette.text.primary,
      transition: 'background-color 9999s ease-in-out 0s',
    },

    // Keyboard focus only. `:focus-visible` leaves mouse clicks alone, so the ring appears
    // for people navigating by keyboard and never as a leftover halo after a click.
    ':focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },

    // Long codes and slugs — "boss.radahn", a package version — should wrap rather than
    // stretch the layout they sit in.
    code: {
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
      fontSize: '0.875em',
      padding: '0.1em 0.35em',
      borderRadius: 6,
      backgroundColor: alpha(theme.palette.text.primary, 0.08),
      overflowWrap: 'anywhere' as const,
    },
  };
}
